import { Link, useParams } from 'react-router-dom';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import StateBlock from '@/components/base/StateBlock';
import RankTable from './components/RankTable';
import ShareBar from './components/ShareBar';
import { getContentById, getRelatedContent, formatRankChange } from '@/utils/content';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getContentById(id) : undefined;

  useDocumentMeta({
    title: item
      ? `${item.title} - 51每日大赛`
      : '内容不存在 - 51每日大赛',
    description: item
      ? `${item.summary}（${item.event} · ${item.date}）`
      : '所请求的内容已下线或不存在，请返回首页浏览最新黑榜与赛事资讯。',
    keywords: item ? item.tags.join(',') : '51每日大赛,今日黑榜,赛事资讯',
  });

  if (!item) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '内容不存在' }]} />
        <div className="mt-10 rounded-lg border border-background-200 bg-background-50">
          <StateBlock
            type="empty"
            title="所请求内容已下线或不存在"
            description="该黑榜或资讯条目可能已被移除，你可以返回首页浏览最新内容。"
          />
          <div className="pb-12 flex justify-center">
            <Link
              to="/"
              className="px-5 py-2.5 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const related = getRelatedContent(item, 4);
  const isBlacklist = item.type === 'blacklist';

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
      <Breadcrumb
        items={[
          { label: '首页', path: '/' },
          {
            label: isBlacklist ? '赛事黑榜' : '赛事资讯',
            path: isBlacklist ? '/category/blacklist' : '/category/news',
          },
          { label: item.title },
        ]}
      />

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
        <article className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-primary-600 text-background-50 whitespace-nowrap">
              {isBlacklist ? '今日黑榜' : '赛事资讯'}
            </span>
            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-900 whitespace-nowrap">
              {item.event}
            </span>
            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-900 whitespace-nowrap">
              {item.category}
            </span>
          </div>

          <h1 className="text-2xl md:text-3xl font-black text-foreground-950 leading-snug tracking-tight">
            {item.title}
          </h1>

          <div className="mt-4 flex items-center gap-4 text-sm text-foreground-500 flex-wrap pb-5 border-b border-background-200">
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-user-line"></i>
              {item.author}
            </span>
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-calendar-line"></i>
              {item.date}
            </span>
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-price-tag-3-line"></i>
              {item.source}
            </span>
            {item.readTime && (
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-time-line"></i>
                约 {item.readTime} 分钟阅读
              </span>
            )}
          </div>

          <div className="mt-6 rounded-lg overflow-hidden bg-background-200">
            <img
              src={item.cover}
              alt={`${item.title} 配图`}
              title={`${item.title} - 51每日大赛`}
              className="w-full h-64 md:h-[400px] object-top object-cover"
            />
          </div>

          {isBlacklist && (
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <p className="text-xs text-foreground-500 mb-1">上榜选手</p>
                <p className="text-lg font-black text-foreground-950">{item.player}</p>
                <p className="text-xs text-foreground-400 mt-0.5">{item.team}</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <p className="text-xs text-foreground-500 mb-1">当前排名</p>
                <p className="text-lg font-black text-foreground-950">No.{item.rank}</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <p className="text-xs text-foreground-500 mb-1">排名变化</p>
                <p className="text-lg font-black text-accent-700">
                  {formatRankChange(item.rankChange)}
                </p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <p className="text-xs text-foreground-500 mb-1">表现指数</p>
                <p className="text-lg font-black text-foreground-950">{item.performanceIndex}</p>
              </div>
            </div>
          )}

          {isBlacklist && item.reason && (
            <div className="mt-6 rounded-lg border-l-4 border-accent-500 bg-accent-50 p-5">
              <p className="text-sm font-bold text-accent-900 mb-1 flex items-center gap-1.5">
                <i className="ri-alert-line"></i>
                上榜原因
              </p>
              <p className="text-sm text-foreground-700 leading-relaxed">{item.reason}</p>
            </div>
          )}

          <div className="mt-6 space-y-4">
            {item.content.map((paragraph, index) => (
              <p key={index} className="text-base text-foreground-700 leading-loose">
                {paragraph}
              </p>
            ))}
          </div>

          {isBlacklist && item.rankTable && item.rankTable.length > 0 && (
            <div className="mt-8">
              <h2 className="text-lg font-bold text-foreground-950 mb-3 flex items-center gap-2">
                <i className="ri-table-line text-primary-600"></i>
                排名变化表
              </h2>
              <RankTable rows={item.rankTable} />
            </div>
          )}

          <div className="mt-8 flex items-center gap-2 flex-wrap">
            {item.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs whitespace-nowrap"
              >
                #{tag}
              </span>
            ))}
          </div>

          <div className="mt-6 pt-6 border-t border-background-200">
            <ShareBar title={item.title} />
          </div>

          <p className="mt-8 text-xs text-foreground-400">
            演示内容，实际数据接入待开发。文中赛事、战队与选手均为虚构示例。
          </p>
        </article>

        <aside className="min-w-0">
          <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-links-line text-primary-600"></i>
              相关内容推荐
            </h2>
            <div className="flex flex-col gap-3">
              {related.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/detail/${rel.id}`}
                  className="group flex gap-3 cursor-pointer"
                >
                  <div className="w-20 h-16 rounded-md overflow-hidden bg-background-200 shrink-0">
                    <img
                      src={rel.cover}
                      alt={`${rel.title} 缩略图`}
                      title={`${rel.title} - 51每日大赛`}
                      className="w-full h-full object-top object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
                      {rel.title}
                    </p>
                    <span className="text-xs text-foreground-400 mt-1 block">{rel.date}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}